import React, { useState, useEffect } from "react";
import { getProducts, getCategories } from "../../api/products";
import { fetchMemberInfo } from "../../api/members";
import PaymentPanel from "./PaymentPanel";
import SearchBar from "../common/components/SearchBar";
import { showToast } from "../common/components/Toast";
import { motion, AnimatePresence } from "framer-motion";
import { matchesChoseong } from "../../utils/search";
import { isSoldOut, isStockExhausted } from "../../utils/stock";

const POS = (user) => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [usedPoints, setUsedPoints] = useState(0);
  const [categories, setCategories] = useState([""]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [toastMessage, setToastMessage] = useState(null);
  // 상품과 카테고리가 함께 도착하기 전까지는 "상품 없음"처럼 보이지 않도록 로딩을 표시한다
  const [isLoading, setIsLoading] = useState(true);

  // 상품 목록 불러오기 및 카테고리 세팅
  // 카테고리 탭은 등록 테이블(API)이 단일 출처다. 상품에서 파생시키면 상품 없는 카테고리가 탭에서 빠지고
  // 순서도 고객 메인과 어긋난다. 기본 선택은 display_order가 가장 앞선 첫 번째 카테고리.
  useEffect(() => {
    const load = async () => {
      const [productList, categoryRows] = await Promise.all([getProducts(), getCategories()]);
      setProducts(productList);

      // 카테고리 API가 실패하면 빈 배열이 온다. 그대로 두면 탭도 상품도 0개가 되어
      // 결제 화면이 멎으므로, 이 경우에만 예전처럼 상품에서 목록을 파생시킨다.
      const categoryNames = categoryRows.length > 0
        ? categoryRows.map((item) => item.category)
        : [...new Set(productList.map((product) => product.category))];

      setCategories(categoryNames);
      if (categoryNames.length > 0) {
        setSelectedCategory(categoryNames[0]);
      }
      setIsLoading(false);
    };

    load();
  }, []);

  // 결제 패널에 상품 추가 및 재고 수량에 따른 토스트 노출
  const addToCart = (product) => {
    setSelectedProducts((prev) => {
      const existingItem = prev.find((item) => item.id === product.id);

      // 재고 초과 시 토스트 노출 후 함수 종료
      if (existingItem && isStockExhausted(product, existingItem.quantity)) {
        setToastMessage("재고 수량을 초과하였습니다."); // ✅ 상태 업데이트만 수행
        return prev;
      }

      if (!existingItem && isSoldOut(product)) {
        setToastMessage("품절된 상품입니다."); // ✅ 상태 업데이트만 수행
        return prev;
      }
  
      return existingItem
        ? prev.map((item) =>
            item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
          )
        : [...prev, { ...product, quantity: 1 }];
    });
  };
  
  // ✅ 상태가 변경될 때만 토스트 메시지를 띄움
  useEffect(() => {
    if (toastMessage) {
      showToast(toastMessage, "fail");
      setToastMessage(null); // ✅ 토스트 메시지를 초기화
    }
  }, [toastMessage]);

  // 장바구니에서 상품 수량 감소 시 아이템 삭제
  const removeFromCart = (product) => {
    setSelectedProducts((prev) =>
      prev
        .map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity - 1 } : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  // 회원 선택
  const handleSelectMember = async (memberId) => {
    const memberInfo = await fetchMemberInfo(memberId);
    setSelectedMember(memberInfo);
  };

  // 포인트 사용 입력
  const handlePointChange = (e) => {
    const value = Number(e.target.value);
    if (value <= (selectedMember?.points || 0)) {
      setUsedPoints(value);
    }
  };

  const trimmedSearchTerm = searchTerm.trim();
  const isSearching = trimmedSearchTerm !== "";

  // 검색 중에는 카테고리와 무관하게 전체 상품에서 찾는다
  const visibleProducts = isSearching
    ? products.filter((product) => matchesChoseong(product.name, trimmedSearchTerm))
    : products.filter((product) => product.category === selectedCategory);

  return (
    <div className="flex h-full rounded-3xl bg-gray-50 border-gray-200 border-[1px] font-body overflow-auto">
      <div className="flex flex-col w-full p-8">
        <div className="mb-4">
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            placeholder="전체 상품에서 검색해 보세요"
          />
        </div>

        {/* 검색 중에는 전체 상품 대상이므로 카테고리 탭을 숨긴다 */}
        {/* flex-wrap: 좁은 화면에서 탭이 가로로 밀려 사라지지 않도록 줄바꿈 (구 overflow-scroll은
            스크롤바 숨김 CSS와 겹쳐 탭 전체가 시야 밖으로 밀리는 현상의 원인이었음) */}
        {!isSearching && (
          <div className="flex flex-wrap border-b border-gray-300 mb-4">
            {categories.filter(Boolean).map((category) => (
              <button
                key={category}
                className={`relative sm:py-1 md:py-2 lg:py-3 sm:px-3 md:px-3 lg:px-4 font-medium sm:text-base md:text-lg lg:text-xl whitespace-nowrap
                  ${selectedCategory === category ? "text-gray-950 font-bold" : "text-gray-500 hover:text-gray-900"}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}

                {/* ✅ 선택된 카테고리에 애니메이션 효과 추가 */}
                {selectedCategory === category && (
                  <motion.div
                    layoutId="category-underline"
                    className="absolute bottom-0 left-0 right-0 h-1 bg-gray-950 rounded-full"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>
        )}

        {isLoading && <p className="text-gray-500">불러오는 중...</p>}

        {/* 상품이 하나도 없을 때 빈 화면만 남으면 고장으로 오해하기 쉬워 사유를 알려준다 */}
        {!isLoading && visibleProducts.length === 0 && (
          <p className="text-gray-500 py-8">
            {isSearching
              ? "검색 결과가 없습니다."
              : "이 카테고리에 등록된 상품이 없습니다."}
          </p>
        )}

        <div className="flex flex-row flex-wrap sm:gap-3 md:gap-4 lg:gap-4">
          {visibleProducts
            .map((product) => (
              <button
                key={product.id}
                disabled={isSoldOut(product)}
                className={`border p-4 rounded-xl shadow flex flex-col justify-between items-start sm:w-[140px] md:w-[160px] lg:w-[180px] aspect-square
                  transition-transform duration-200 ease-in-out transform hover:scale-105
                  ${isSoldOut(product) ? "bg-gray-300 text-white cursor-not-allowed" : "bg-white"}`}
                onClick={() => addToCart(product)}
              >
                {/* 검색 중에는 카테고리 접두어로 유사/동일명 상품을 구분한다 (표시용 — 장바구니에는 원본 name 전달) */}
                <h3 className="sm:text-sm md:text-base lg:text-lg font-semibold text-left">
                  {isSearching ? `[${product.category}] ${product.name}` : product.name}
                </h3>
                {isSoldOut(product) ? (
                  <p className="font-bold">품절</p>
                ) : (
                  <p className="sm:text-sm md:text-lg lg:text-xl">{product.price.toLocaleString()}원</p>
                )}
              </button>
            ))}
        </div>
      </div>

      <PaymentPanel
        cartItems={selectedProducts}
        setCartItems={setSelectedProducts}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
        selectedMember={selectedMember}
        setSelectedMember={setSelectedMember}
        usedPoints={usedPoints}
        setUsedPoints={setUsedPoints}
        handleSelectMember={handleSelectMember}
        handlePointChange={handlePointChange}
        admin={user} 
      />
    </div>
  );
};

export default POS;